import { useState } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { useProfile } from '../../../hooks/useProfile';
import type { UpdateProfileData } from '../../../lib/database.types';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import ProfileHeader from './components/ProfileHeader';
import StatsRow from './components/StatsRow';
import ReadingListTab from './components/ReadingListTab';
import RecentlyReadTab from './components/RecentlyReadTab';

export default function ReaderProfilePage() {
  const { user } = useAuth();
  const { profile, loading, updateProfile } = useProfile(user?.id);
  const [activeTab, setActiveTab] = useState<'reading-list' | 'recently-read'>('reading-list');

  const handleAvatarUpdate = async (url: string) => {
    await updateProfile({ avatar_url: url } as UpdateProfileData);
  };

  const handleUsernameUpdate = async (newName: string) => {
    await updateProfile({ display_name: newName, username: newName } as UpdateProfileData);
  };

  const handleBioUpdate = async (newBio: string) => {
    await updateProfile({ bio: newBio } as UpdateProfileData);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col">
        <Navbar />
        <div className="flex-1 max-w-6xl mx-auto w-full px-6 py-12 pt-28">
          <div className="animate-pulse">
            <div className="h-48 bg-zinc-900 rounded-lg mb-8"></div>
            <div className="grid grid-cols-4 gap-6 mb-12">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-24 bg-zinc-900 rounded-lg"></div>
              ))}
            </div>
            <div className="h-96 bg-zinc-900 rounded-lg"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Profile not found</h2>
            <p className="text-zinc-400">Unable to load profile data.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-12 pt-28">
        <ProfileHeader
          displayName={profile.display_name}
          username={profile.username}
          bio={profile.bio || ''}
          avatarUrl={profile.avatar_url || ''}
          joinDate={profile.join_date ?? ''}
          onAvatarUpdate={handleAvatarUpdate}
          onUsernameUpdate={handleUsernameUpdate}
          onBioUpdate={handleBioUpdate}
        />

        <StatsRow />

        <div className="mb-8">
          <div className="flex gap-2 border-b border-zinc-800">
            <button
              onClick={() => setActiveTab('reading-list')}
              className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'reading-list'
                  ? 'text-red-500 border-b-2 border-red-500'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              Reading List
            </button>
            <button
              onClick={() => setActiveTab('recently-read')}
              className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'recently-read'
                  ? 'text-red-500 border-b-2 border-red-500'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              Recently Read
            </button>
          </div>
        </div>

        {activeTab === 'reading-list' ? <ReadingListTab /> : <RecentlyReadTab />}
      </main>

      <Footer />
    </div>
  );
}
